import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Stack } from "@mui/material";
import PropTypes from "prop-types";
import LoadingOverlay from "../../../../../Shared/LoadingOverlay";
import ChartEmptyState from "../../../../../Shared/chartEmptyState";
import { CHART_IDS, VENDOR_STATS_CHART_LIMITS } from "../../../../../../config/featureConfig";
import vendorStatsService from "../../../../../../services/vendorStatsService";
import TopSearchedCategoryCard from "./TopSearchedCategoryCard";
import { useDashboardChartsDate } from "../../context/dashboardChartsDateContext";

const TopSearchedCategories = ({ embedInGrid = false, onOpenInTabView }) => {
  const { customRange } = useDashboardChartsDate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const { grid: gridLimit, list: listViewLimit } = VENDOR_STATS_CHART_LIMITS[CHART_IDS.TOP_CATEGORIES_BY_SEARCH];
  const fetchLimit = embedInGrid ? gridLimit : listViewLimit;

  const maxVal = useMemo(() => Math.max(1, ...items.map((i) => Number(i.clickCount) || 0)), [items]);

  const runFetch = useCallback(async (payload) => {
    setLoading(true);
    try {
      const res = await vendorStatsService.getTopCategoriesByClick(payload);
      setItems(res.items ?? []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const payload = { limit: fetchLimit };
    if (customRange?.start && customRange?.end) {
      payload.startDate = customRange.start.format("YYYY-MM-DD");
      payload.endDate = customRange.end.format("YYYY-MM-DD");
    }
    runFetch(payload);
  }, [customRange, embedInGrid, fetchLimit, runFetch]);

  const hasData = useMemo(() => items.some((i) => Number(i.clickCount) > 0), [items]);

  const visibleItems = useMemo(() => {
    if (embedInGrid) return items.slice(0, gridLimit);
    return items;
  }, [embedInGrid, gridLimit, items]);

  return (
    <Box sx={{ position: "relative", minHeight: 200 }}>
      <LoadingOverlay open={loading} />
      <Stack spacing={2}>
        {!loading && !hasData ? (
          <ChartEmptyState minHeight={220} />
        ) : !loading ? (
          <Stack spacing={1.25}>
            {visibleItems.map((item, idx) => (
              <TopSearchedCategoryCard
                key={item.categoryListId ?? `${item.label}-${idx}`}
                label={item.label || `Categoria ${item.categoryListId ?? idx}`}
                value={Number(item.clickCount) || 0}
                maxValue={maxVal}
                onActivate={embedInGrid ? onOpenInTabView : undefined}
              />
            ))}
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
};

TopSearchedCategories.propTypes = {
  embedInGrid: PropTypes.bool,
  onOpenInTabView: PropTypes.func,
};

export default TopSearchedCategories;
